// Navigation will be configured separately to avoid Jest issues
// import { createSharedPathnamesNavigation } from 'next-intl/navigation';

import { captureError, captureMessage, buildTags, toSerializable } from '@/lib/monitoring/logger';

export const locales = ['en-GB', 'en-IE', 'de-DE', 'de-AT', 'de-CH'] as const;
export type Locale = typeof locales[number];

export const defaultLocale: Locale = 'en-GB';

// Locale metadata for validation and display
interface LocaleMetadataEntry {
  name: string;
  nativeName: string;
  dateFormat: string;
  timeFormat: string;
  numberFormat: string;
  currency: string;
  timezone: string;
  flag: string;
}

export const localeMetadata: Record<Locale, LocaleMetadataEntry> = {
  'en-GB': {
    name: 'English (United Kingdom)',
    nativeName: 'English (UK)',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: '24h',
    numberFormat: '1,234.56',
    currency: 'GBP',
    timezone: 'Europe/London',
    flag: '🇬🇧'
  },
  'en-IE': {
    name: 'English (Ireland)',
    nativeName: 'English (Ireland)',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: '24h',
    numberFormat: '1,234.56',
    currency: 'GBP',
    timezone: 'Europe/Dublin',
    flag: '🇮🇪'
  },
  'de-DE': {
    name: 'Deutsch (Deutschland)',
    nativeName: 'Deutsch (Deutschland)',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: '24h',
    numberFormat: '1.234,56',
    currency: 'EUR',
    timezone: 'Europe/Berlin',
    flag: '🇩🇪'
  },
  'de-AT': {
    name: 'Deutsch (Österreich)',
    nativeName: 'Deutsch (Österreich)',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: '24h',
    numberFormat: '1.234,56',
    currency: 'EUR',
    timezone: 'Europe/Vienna',
    flag: '🇦🇹'
  },
  'de-CH': {
    name: 'Deutsch (Schweiz)',
    nativeName: 'Deutsch (Schweiz)',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: '24h',
    numberFormat: '1.234,56',
    currency: 'CHF',
    timezone: 'Europe/Zurich',
    flag: '🇨🇭'
  }
} as const;

// Helper function to deep merge objects
function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  if (!source) return target;
  
  Object.keys(source).forEach(key => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {};
      deepMerge(target[key] as Record<string, unknown>, source[key] as Record<string, unknown>);
    } else {
      target[key] = source[key];
    }
  });
  
  return target;
}

// Validation function for locale format
export function isValidLocale(locale: string): locale is Locale {
  return locales.includes(locale as Locale);
}

// Navigation exports will be handled separately
// export const { Link, redirect, usePathname, useRouter } = 
//   createSharedPathnamesNavigation({ locales });

// Locale detection from various sources
export interface LocaleDetectionContext {
  acceptLanguage?: string;
  queryParam?: string;
  tenantPreference?: string;
  userPreference?: string;
}

export function detectBestLocale(context: LocaleDetectionContext): Locale {
  // Priority order: query param > tenant preference > user preference > accept-language > default
  
  if (context.queryParam && isValidLocale(context.queryParam)) {
    return context.queryParam;
  }
  
  if (context.tenantPreference && isValidLocale(context.tenantPreference)) {
    return context.tenantPreference;
  }
  
  if (context.userPreference && isValidLocale(context.userPreference)) {
    return context.userPreference;
  }
  
  if (context.acceptLanguage) {
    const parsed = parseAcceptLanguage(context.acceptLanguage);
    for (const candidate of parsed) {
      if (isValidLocale(candidate)) {
        return candidate;
      }
      // Try fallback (e.g., 'de' -> 'de-DE')
      const fallback = getFallbackLocale(candidate);
      if (fallback && isValidLocale(fallback)) {
        return fallback;
      }
    }
  }
  
  return defaultLocale;
}

// Parse Accept-Language header
function parseAcceptLanguage(header: string): string[] {
  return header
    .split(',')
    .map(lang => {
      const [locale, quality] = lang.trim().split(';q=');
      return {
        locale: locale.trim(),
        quality: quality ? parseFloat(quality) : 1.0
      };
    })
    .sort((a, b) => b.quality - a.quality)
    .map(item => item.locale);
}

// Get fallback locale for language codes
function getFallbackLocale(languageCode: string): Locale | null {
  const fallbacks: Record<string, Locale> = {
    'en': 'en-GB',
    'de': 'de-DE'
  };

  return fallbacks[languageCode] || null;
}

/**
 * Supported locales for audit pack generation
 * This is a subset of the full locales array
 */
export type AuditPackLocale = 'en-GB' | 'de-DE';

/**
 * Normalize a locale string to a supported audit pack locale
 * Falls back to en-GB for unsupported locales
 *
 * @param locale - Any locale string
 * @returns A valid AuditPackLocale
 */
export function normalizeToAuditPackLocale(locale: string): AuditPackLocale {
  // Direct match
  if (locale === 'en-GB' || locale === 'de-DE') {
    return locale;
  }

  // Language-based fallbacks
  if (locale.startsWith('de')) {
    return 'de-DE';
  }

  // Default to English for all other locales
  return 'en-GB';
}

// Namespace configuration for translation loading
export const translationNamespaces = [
  'common',
  'auth',
  'contact',
  'dashboard',
  'instruments',
  'certificates',
  'forms',
  'alerts',
  'notifications',
  'audit',
  'marketing',
  'nav',
  'oot',
  'settings',
  'protected',
  'emails',
  'errors',
  'trust',
  'legal',
  'roles',
  'offline',
  'onboarding',
  'docs',
  'cert-import'
] as const;

export type TranslationNamespace = typeof translationNamespaces[number];

async function loadMessagesForLocale(locale: Locale, namespace?: TranslationNamespace): Promise<Record<string, unknown>> {
  if (namespace) {
    const messagesModule = await import(`../../locales/${locale}/${namespace}.json`);
    const data = messagesModule.default as Record<string, unknown>;
    return ((data?.[namespace] as Record<string, unknown>) ?? data ?? {}) as Record<string, unknown>;
  }

  const allMessages: Record<string, unknown> = {};
  for (const ns of translationNamespaces) {
    try {
      const nsMessages = await import(`../../locales/${locale}/${ns}.json`);
      const payload = (nsMessages.default ?? {}) as Record<string, unknown>;
      const namespacedSection = extractNamespaceSection(payload, ns);

      if (namespacedSection) {
        if (!allMessages[ns] || typeof allMessages[ns] !== 'object' || Array.isArray(allMessages[ns])) {
          allMessages[ns] = {};
        }
        deepMerge(allMessages[ns] as Record<string, unknown>, namespacedSection);
      }

      // Deep merge to preserve nested structure (legacy files may contribute extra keys)
      deepMerge(allMessages, payload);
    } catch {
      // Skip missing namespace files
      captureMessage('i18n.config.missing-namespace', 'warning', {
        tags: buildTags('i18n-config', 'missing-namespace'),
        extra: toSerializable({ locale, namespace: ns })
      });
    }
  }

  return allMessages;
}

// Get messages for a specific locale and namespace
export async function getMessages(locale: Locale, namespace?: TranslationNamespace) {
  try {
    const localeMessages = await loadMessagesForLocale(locale, namespace);
    if (locale === defaultLocale) {
      return localeMessages;
    }

    const fallbackMessages = await loadMessagesForLocale(defaultLocale, namespace);
    return deepMerge(fallbackMessages, localeMessages);
  } catch (error) {
    captureError(error, {
      tags: buildTags('i18n-config', 'load-messages'),
      extra: toSerializable({ locale, namespace })
    });
    
    // Fallback to English
    if (locale !== defaultLocale) {
      return getMessages(defaultLocale, namespace);
    }
    
    return {};
  }
}

// Get multiple namespaces efficiently
export async function getPageMessages(locale: Locale, namespaces: TranslationNamespace[]) {
  const messagePromises = namespaces.map(ns => 
    getMessages(locale, ns).then(messages => ({ [ns]: messages }))
  );
  
  const results = await Promise.all(messagePromises);
  return Object.assign({}, ...results);
}

function extractNamespaceSection(payload: Record<string, unknown>, namespace: string): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const value = payload[namespace];
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
}
